import { put, all, takeLatest } from 'redux-saga/effects';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchReducerActions, createFetchAction, createSaga, fetchInitialState } from 'store/utils';
import * as notificationApi from 'api/notification';
import store from 'store';

export const fetch_new_notifications = createFetchAction('fetch_new_notifications');
export const fetch_official_notifications = createFetchAction('fetch_official_notifications');
export const fetch_main_notifications = createFetchAction('fetch_main_notifications');
export const fetch_notifications = createFetchAction('fetch_notifications');
export const read_notifications = createFetchAction('read_notifications');
export const delete_notifications = createFetchAction('delete_notifications');

interface InitialState {
  newNotifications: object | any;
  officialNotifications: object | any;
  mainNotifications: object | any;
  notifications: object | any;
  readNotifications: object | any;
  deleteNotifications: object | any;
}

const initialState: InitialState = {
  newNotifications: { ...fetchInitialState, data: null },
  officialNotifications: { ...fetchInitialState, data: null },
  mainNotifications: { ...fetchInitialState, data: null },
  notifications: { ...fetchInitialState, data: null },
  readNotifications: { ...fetchInitialState },
  deleteNotifications: { ...fetchInitialState },
};

const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    ...fetchReducerActions(fetch_new_notifications, 'newNotifications'),
    ...fetchReducerActions(fetch_official_notifications, 'officialNotifications'),
    ...fetchReducerActions(fetch_main_notifications, 'mainNotifications'),
    set_main_notifications: (state, { payload }: PayloadAction<{ notiId: string }>) => {
      const { notiId } = payload;

      if (!Array.isArray(state.mainNotifications.data?.list)) return;
      state.mainNotifications.data.list = state.mainNotifications.data.list.map((item: any) => {
        if (item._id === notiId) item.isRead = 1;
        return item;
      });
    },
    remove_main_notification: (state, { payload }: PayloadAction<{ notiId: string }>) => {
      const { notiId } = payload;

      if (!Array.isArray(state.mainNotifications.data?.list)) return;
      state.mainNotifications.data.list = state.mainNotifications.data.list.filter((item: any) => {
        return item._id !== notiId;
      });
    },
    ...fetchReducerActions(fetch_notifications, 'notifications'),
    set_notifications: (
      state,
      { payload }: PayloadAction<{ notiId?: string; notiIdArr?: string[] }>,
    ) => {
      const { notiId, notiIdArr } = payload;

      if (!Array.isArray(state.notifications.data?.list)) return;
      state.notifications.data.list = state.notifications.data.list.map((item: any) => {
        if (notiId) {
          return item._id === notiId ? { ...item, isRead: 1 } : item;
        }
        if (notiIdArr) {
          return notiIdArr.includes(item._id) ? { ...item, isRead: 1 } : item;
        }
        return item;
      });
    },
    remove_notification: (
      state,
      { payload }: PayloadAction<{ notiId?: string; notiIdArr?: string[] }>,
    ) => {
      const { notiId, notiIdArr } = payload;

      if (!Array.isArray(state.notifications.data?.list)) return;
      state.notifications.data.list = state.notifications.data.list.filter((item: any) => {
        if (notiId) return item._id !== notiId;
        if (notiIdArr) return !notiIdArr.includes(item._id);
        return item;
      });
    },
    ...fetchReducerActions(read_notifications, 'readNotifications'),
    ...fetchReducerActions(delete_notifications, 'deleteNotifications'),
  },
});

export const actions: any = slice.actions;

export function* notificationSaga() {
  yield all([
    takeLatest(
      actions.fetch_new_notifications_request,
      createSaga(actions, 'fetch_new_notifications', notificationApi.fetchNewNotificationsCount),
    ),
    takeLatest(
      actions.fetch_official_notifications_request,
      createSaga(
        actions,
        'fetch_official_notifications',
        notificationApi.fetchOfficialNotifications,
      ),
    ),
    takeLatest(
      actions.fetch_main_notifications_request,
      createSaga(actions, 'fetch_main_notifications', notificationApi.fetchMainNotifications, {
        isApiLoading: false,
      }),
    ),
    takeLatest(
      actions.fetch_notifications_request,
      createSaga(actions, 'fetch_notifications', notificationApi.fetchNotifications),
    ),
    takeLatest(
      actions.read_notifications_request,
      createSaga(actions, 'read_notifications', notificationApi.readNotifications, {
        success: (_: any, payload: { notificationIdArr: any[] }) => {
          store.dispatch(actions.set_notifications({ notiIdArr: payload.notificationIdArr }));
        },
      }),
    ),
    takeLatest(
      actions.delete_notifications_request,
      createSaga(actions, 'delete_notifications', notificationApi.deleteNotifications, {
        success: (_: any, payload: { notificationIdArr: any[] }) => {
          store.dispatch(actions.remove_notification({ notiIdArr: payload.notificationIdArr }));
        },
      }),
    ),
  ]);
}

export default slice.reducer;
